import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { fileUrl, formatBRL, formatKM, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Gauge, Calendar, Fuel, Settings2, Palette, MapPin, MessageCircle, Loader2, ArrowLeft, Send, ShieldCheck, BadgeCheck, Star, TrendingDown, TrendingUp, Check, ListChecks, QrCode, Share2, Link2, Facebook, Twitter, Sparkles, ChevronDown } from "lucide-react";

export default function AnuncioDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [explain, setExplain] = useState("");
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainLoading, setExplainLoading] = useState(false);

  const explainCar = async () => {
    if (explain) { setExplainOpen((o) => !o); return; }
    setExplainOpen(true);
    setExplainLoading(true);
    try {
      const { data } = await api.post(`/listings/${id}/explain`, {});
      setExplain(data.explanation || "");
    } catch {
      toast.error("Não consegui gerar o resumo agora. Tente novamente.");
      setExplainOpen(false);
    } finally { setExplainLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/listings/${id}`)
      .then(({ data }) => setCar(data))
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar anúncio"))
      .finally(() => setLoading(false));
  }, [id]);

  const sendMessage = async () => {
    if (!user) { navigate("/entrar", { state: { from: `/anuncio/${id}` } }); return; }
    if (!msg.trim()) { toast.error("Escreva uma mensagem."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/listings/${id}/message`, { text: msg.trim() });
      toast.success("Mensagem enviada ao vendedor!");
      navigate(`/mensagens?c=${data.conversation_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blaze" /></div>;

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="anuncio-error">
        <h1 className="font-heading font-800 text-2xl">Anúncio não encontrado</h1>
        <p className="mt-3 text-muted-foreground">{error}</p>
        <Button className="mt-6 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/buscar")} data-testid="error-back-btn">Ver outros carros</Button>
      </div>
    );
  }

  if (car?.qr_disabled) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="qr-disabled">
        <div className="w-16 h-16 rounded-sm bg-secondary flex items-center justify-center mx-auto"><QrCode className="w-8 h-8 text-muted-foreground" strokeWidth={1.6} /></div>
        <h1 className="mt-5 font-heading font-800 text-2xl">QR Code desativado</h1>
        <p className="mt-3 text-muted-foreground">Este QR Code não está mais ativo no momento. Mas temos muitos outros carros à venda no PicCar.</p>
        <Button className="mt-6 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/buscar")} data-testid="qr-disabled-btn">Ver carros disponíveis</Button>
      </div>
    );
  }

  const imgs = car.images?.length ? car.images.map(fileUrl) : ["https://images.unsplash.com/photo-1630356220662-4d9804d3a987"];
  const specs = [
    { icon: Calendar, label: "Ano", value: car.ano_fabricacao && car.ano_fabricacao !== car.ano ? `${car.ano_fabricacao}/${car.ano}` : car.ano },
    { icon: Gauge, label: "KM", value: formatKM(car.km) },
    { icon: Settings2, label: "Câmbio", value: car.cambio || "—" },
    { icon: Fuel, label: "Combustível", value: car.combustivel || "—" },
    { icon: Palette, label: "Cor", value: car.cor || "—" },
  ];
  const fipeDiff = car.fipe_price ? ((car.preco - car.fipe_price) / car.fipe_price) * 100 : null;

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/anuncio/${id}` : "";
  const shareText = `${car.marca} ${car.modelo} por ${formatBRL(car.preco)} no PicCar`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiado! Cole onde quiser para compartilhar.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, text: shareText, url: shareUrl }); } catch {}
    } else { copyLink(); }
  };
  const shareLinks = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}` },
    { key: "facebook", label: "Facebook", icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: "twitter", label: "X (Twitter)", icon: Twitter, url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
    { key: "telegram", label: "Telegram", icon: Send, url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="anuncio-page">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-obsidian" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-sm border-obsidian/15 font-700" data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" /> Compartilhar
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-2 rounded-sm" data-testid="share-menu">
            <button onClick={copyLink} className="w-full flex items-center gap-3 px-3 py-2 text-sm font-600 rounded-sm hover:bg-secondary transition-colors" data-testid="share-copy">
              <Link2 className="w-4 h-4 text-blaze" /> Copiar link
            </button>
            {shareLinks.map((s) => (
              <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-600 rounded-sm hover:bg-secondary transition-colors" data-testid={`share-${s.key}`}>
                <s.icon className="w-4 h-4 text-blaze" /> {s.label}
              </a>
            ))}
            {typeof navigator !== "undefined" && navigator.share && (
              <button onClick={nativeShare} className="w-full flex items-center gap-3 px-3 py-2 text-sm font-600 rounded-sm hover:bg-secondary transition-colors" data-testid="share-native">
                <Share2 className="w-4 h-4 text-blaze" /> Mais opções…
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* GALLERY (public) */}
        <div className="lg:col-span-7">
          <div className="relative bg-white border border-border rounded-sm overflow-hidden aspect-[4/3]">
            <img src={imgs[activeImg]} alt={car.modelo} className={`w-full h-full object-cover ${car.status === "sold" ? "grayscale opacity-80" : ""}`} data-testid="anuncio-main-img" />
            {car.status === "sold" && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="bg-blaze text-white font-poster text-4xl px-6 py-2 -rotate-6 shadow-xl">VENDIDO</span>
              </div>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {imgs.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-sm overflow-hidden border-2 ${i === activeImg ? "border-blaze" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO + MESSAGING */}
        <div className="lg:col-span-5">
          <p className="overline text-blaze mb-2">{car.marca}</p>
          <h1 className="font-heading font-900 text-3xl leading-tight">{car.marca} {car.modelo}</h1>
          {car.versao && <p className="mt-1 text-sm text-muted-foreground font-600" data-testid="detalhe-versao">{car.versao}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {car.blindado && <span className="inline-flex items-center gap-1 bg-obsidian text-white text-[11px] font-700 px-2 py-1 rounded-sm" data-testid="detalhe-blindado"><ShieldCheck className="w-3 h-3" /> Blindado</span>}
            {car.qr_verified && <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[11px] font-700 px-2 py-1 rounded-sm" data-testid="detalhe-qr-verificado"><QrCode className="w-3 h-3" /> QR conferido pelo vendedor</span>}
          </div>
          {car.status === "sold" && (
            <div className="mt-3 inline-flex items-center gap-2 bg-blaze text-white font-700 text-sm px-3 py-1.5 rounded-sm" data-testid="detalhe-vendido">
              <BadgeCheck className="w-4 h-4" /> Este carro já foi vendido
            </div>
          )}
          <p className="mt-4 font-heading font-900 text-4xl text-obsidian">{formatBRL(car.preco)}</p>

          {car.fipe_price != null && (
            <div className="mt-3 flex items-center justify-between gap-3 bg-white border border-border rounded-sm px-4 py-3" data-testid="detalhe-fipe">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-blaze" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Tabela FIPE{car.fipe_ref ? ` · ${car.fipe_ref}` : ""}</p>
                  <p className="text-sm font-800 text-obsidian">{formatBRL(car.fipe_price)}</p>
                </div>
              </div>
              {fipeDiff != null && (fipeDiff <= 0 ? (
                <span className="inline-flex items-center gap-1 text-xs font-800 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm">
                  <TrendingDown className="w-3.5 h-3.5" /> {Math.abs(fipeDiff).toFixed(0)}% abaixo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-800 bg-amber-100 text-amber-700 px-2 py-1 rounded-sm">
                  <TrendingUp className="w-3.5 h-3.5" /> {fipeDiff.toFixed(0)}% acima
                </span>
              ))}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            {specs.map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-sm p-3 flex items-center gap-3">
                <s.icon className="w-4 h-4 text-blaze" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
                  <p className="text-sm font-700">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4" data-testid="explain-section">
            <button onClick={explainCar} data-testid="explain-btn"
              className="w-full flex items-center justify-between gap-2 bg-obsidian text-white rounded-sm px-4 py-3 hover:bg-blaze transition-colors">
              <span className="flex items-center gap-2 font-700 text-sm">
                <Sparkles className="w-4 h-4 text-blaze" /> Explique esse carro pra mim
              </span>
              {explainLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ChevronDown className={`w-4 h-4 transition-transform ${explainOpen ? "rotate-180" : ""}`} />}
            </button>
            <motion.div initial={false} animate={{ height: explainOpen ? "auto" : 0, opacity: explainOpen ? 1 : 0 }}
              className="overflow-hidden">
              {explainLoading ? (
                <div className="mt-2 bg-white border border-border rounded-sm p-4 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blaze" /> Analisando o anúncio...
                </div>
              ) : explain ? (
                <div className="mt-2 bg-white border border-border rounded-sm p-4" data-testid="explain-content">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-blaze" /> Resumo do Assistente PicCar</p>
                  <p className="text-sm leading-relaxed whitespace-pre-line text-obsidian">{explain}</p>
                  <p className="mt-3 text-[11px] text-muted-foreground">Gerado por IA a partir dos dados do anúncio. Confirme sempre as informações com o vendedor.</p>
                </div>
              ) : null}
            </motion.div>
          </div>

          {(car.cidade || car.estado) && (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="w-4 h-4 text-blaze" /> {car.cidade}{car.estado ? ` - ${car.estado}` : ""}</p>
          )}
          {car.descricao && (
            <div className="mt-4 bg-white border border-border rounded-sm p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Descrição</p>
              <p className="text-sm leading-relaxed whitespace-pre-line">{car.descricao}</p>
            </div>
          )}

          {car.features?.length > 0 && (
            <div className="mt-4 bg-white border border-border rounded-sm p-4" data-testid="detalhe-opcionais">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5 text-blaze" /> Opcionais</p>
              <div className="flex flex-wrap gap-2">
                {car.features.map((op) => (
                  <span key={op} className="inline-flex items-center gap-1.5 text-sm font-600 bg-secondary/60 border border-border px-2.5 py-1.5 rounded-sm">
                    <Check className="w-3.5 h-3.5 text-blaze" strokeWidth={3} /> {op}
                  </span>
                ))}
              </div>
            </div>
          )}

          {car.condicoes?.length > 0 && (
            <div className="mt-4 bg-white border border-border rounded-sm p-4" data-testid="detalhe-condicoes">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-blaze" /> Condições</p>
              <div className="flex flex-wrap gap-2">
                {car.condicoes.map((op) => (
                  <span key={op} className="inline-flex items-center gap-1.5 text-sm font-600 bg-secondary/60 border border-border px-2.5 py-1.5 rounded-sm">
                    <Check className="w-3.5 h-3.5 text-blaze" strokeWidth={3} /> {op}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* MESSAGE AREA (contato protegido) */}
          {car.is_owner ? (
            <div className="mt-6 bg-secondary/60 rounded-sm p-5 text-center" data-testid="owner-note">
              <p className="text-sm text-muted-foreground">Este é o seu anúncio. As mensagens dos interessados aparecem em <button className="font-700 text-obsidian underline" onClick={() => navigate("/mensagens")}>Mensagens</button>.</p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-obsidian text-white rounded-sm p-6" data-testid="message-box">
              <div className="flex items-center gap-2 mb-1"><MessageCircle className="w-5 h-5 text-blaze" /><span className="font-700">Falar com {car.seller_name?.split(" ")[0] || "o vendedor"}</span></div>
              <p className="text-sm text-white/60 mb-4">Converse com segurança pelo chat do PicCar — sem expor telefone.</p>
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder={user ? "Olá! Tenho interesse neste carro. Ainda está disponível?" : "Entre para enviar uma mensagem ao vendedor."}
                className="bg-white text-obsidian rounded-sm min-h-24 border-0" data-testid="message-input" disabled={!user} />
              <Button className="mt-3 w-full h-12 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm" onClick={sendMessage} disabled={sending} data-testid="send-message-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {user ? "Enviar mensagem" : "Entrar para enviar"}
              </Button>
              <p className="mt-3 flex items-center gap-1 text-xs text-white/40"><ShieldCheck className="w-3.5 h-3.5" /> Contato protegido — o vendedor responde pelo chat.</p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
